/**
 * The token of a candidate's document upload link.
 *
 * The emailed link is `/documents/open.html#<token>`. The token is in the
 * fragment because a fragment is never sent to a server, so it cannot end up
 * in a request log. That small static page takes it, leaves it in this tab's
 * session storage and replaces itself with `/documents/upload`, so the upload
 * page is opened at a clean address: the browser's history list, and the
 * upload page's own navigation entry, never hold the token. (A link in the
 * older form, `/documents/upload#<token>`, still works: the fragment is taken
 * and the address cleaned, as before.)
 *
 * On the upload page the token is taken out before anything else can read it,
 * and from then on it lives in this module's memory only, never back in an
 * address, and is sent to the API as a header. Storage, exactly: the token is
 * in sessionStorage only between the two documents of one tab, for the length
 * of one navigation, and is removed by the first script the next page runs,
 * whichever page that turns out to be. It is never in localStorage and never
 * written by the bundle.
 *
 * Two places take it out, and the first one to run wins:
 *
 *   1. An inline script at the top of index.html, ahead of every other script
 *      on the page. It empties the session storage key (or reads the
 *      fragment) and hands the token over in one window property, which is
 *      read here and deleted.
 *   2. This module, for when that script did not run: the dev server's first
 *      paint aside, that is tests. It is imported first by src/index.tsx, ahead
 *      of the logger, so nothing in the bundle that reports addresses starts
 *      while the token is still in one. It must stay free of imports for that
 *      to hold.
 *
 * The cost, on purpose: reloading the page loses the token, and the candidate
 * opens the link from their email again.
 *
 * CLEANING THE ADDRESS IS NOT ENOUGH ON ITS OWN. The browser keeps the address
 * a page was opened with, fragment and token included, in its navigation
 * timing entry (`performance.getEntriesByType("navigation")[0].name`) for as
 * long as the page is open, and any script on the page can read it there. That
 * cannot be cleaned, which is why the link opens the static page first, and,
 * for a link in the older form that still lands here with its fragment, why
 * nothing from another origin may load on this route at all: index.html keeps
 * Stripe.js and the session recorder off it, and a new third-party tag has to
 * be kept off it the same way.
 *
 * A HEADER IS NOT PRIVATE EITHER, which is the second reason this file loads
 * first. The app's logger (./logger-init) starts Application Insights with
 * fetch tracking and request-header tracking both on: every `fetch` the page
 * makes after that is reported to Azure with its headers, and the token is one
 * of them. So this module also keeps the browser's own `fetch`, taken before
 * the logger wraps `window.fetch`, and the upload page sends every request
 * through it. Those requests are then invisible to the logger: no address, no
 * headers. Never call `window.fetch`, XHR or the app's API layer from that page.
 */
export const DOCUMENT_UPLOAD_PATH = "/documents/upload";

/** The header every call from the upload page carries the token in. */
export const DOCUMENT_LINK_HEADER = "X-Document-Link";

/** The property index.html's capture script leaves the token in. */
export const DOCUMENT_LINK_HANDOFF = "__skyhireDocumentLink";

/** The page an emailed link opens first, which hands the token to the upload page. */
export const DOCUMENT_OPEN_PATH = "/documents/open.html";

/**
 * Where that page leaves the token for the upload page: this tab's session
 * storage, for the instant between the two pages. Whoever reads it removes it.
 */
export const DOCUMENT_LINK_STORAGE_KEY = "skyhire.documentLink";

/** What a token looks like. Anything else found in that place is not one. */
const TOKEN_SHAPE = /^[A-Za-z0-9._-]{20,300}$/;

let token: string | null = null;

/**
 * The browser's own fetch, as it was before anything in the bundle could wrap
 * it. Captured once, when this module loads.
 */
const browserFetch: typeof fetch | null =
  typeof window !== "undefined" && typeof window.fetch === "function"
    ? window.fetch.bind(window)
    : null;

/**
 * `fetch` for the candidate's upload page, and for nothing else. It never goes
 * through `window.fetch` as it is now, only through the one captured above, so
 * telemetry that wraps `window.fetch` later does not see these requests.
 */
export const nativeFetch: typeof fetch = (input, init) =>
  browserFetch
    ? browserFetch(input, init)
    : Promise.reject(new TypeError("This browser cannot make the request."));

interface AddressLike {
  pathname: string;
  hash: string;
}

/** The browser's session storage, or nothing where it is refused. */
function sessionStore(): Pick<Storage, "getItem" | "removeItem"> | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Takes the token from the hand-off property, or failing that from where the
 * opening page left it, or from the fragment, and leaves it in none of them.
 * Does nothing on any other page.
 */
export function captureDocumentLink(
  location: AddressLike = window.location,
  history: Pick<History, "replaceState"> = window.history,
  handoff: Record<string, unknown> = window as unknown as Record<string, unknown>,
  storage: Pick<Storage, "getItem" | "removeItem"> | null = sessionStore(),
): void {
  // Normally index.html's script has emptied this already. Read and removed
  // here as well, and on every page, not only the upload page: a token the
  // opening page stored must not outlive a navigation that never arrived.
  let held: string | null = null;
  try {
    held = storage?.getItem(DOCUMENT_LINK_STORAGE_KEY) ?? null;
    if (held !== null) storage?.removeItem(DOCUMENT_LINK_STORAGE_KEY);
  } catch {
    // Storage that cannot be read holds nothing for this page.
  }

  if (location.pathname.replace(/\/+$/, "") !== DOCUMENT_UPLOAD_PATH) return;

  const handed = handoff[DOCUMENT_LINK_HANDOFF];
  // Deleted whether or not it is usable, so nothing later can pick it up.
  delete handoff[DOCUMENT_LINK_HANDOFF];
  if (typeof handed === "string" && handed.trim()) token = handed.trim();
  if (!token && held && TOKEN_SHAPE.test(held)) token = held;

  const fragment = location.hash.replace(/^#/, "").trim();
  if (!fragment) return;
  if (!token) token = fragment;
  try {
    history.replaceState(null, "", location.pathname);
  } catch {
    // The token is still held, and the page still works. The address could not
    // be cleaned, which is no reason to turn the candidate away.
  }
}

/** The token this page was opened with, or null when it was opened without one. */
export function documentLinkToken(): string | null {
  return token;
}

/** For tests: forget the token, as a fresh page load would. */
export function forgetDocumentLink(): void {
  token = null;
}

if (typeof window !== "undefined") captureDocumentLink();
